import React, { useEffect, useState, useCallback } from "react";

const ICONS = [
  "₿",  // Bitcoin
  "🎁", // Regalo
  "🎄", // Árbol de Navidad
  "⭐", // Estrella
  "🎅", // Santa Claus
  "❄️", // Copo de nieve
  "🔔", // Campana
  "🕯️", // Vela
];

function createShuffledDeck() {
  const pairs = ICONS.slice(0, 8); // 8 pares
  const deck = [];

  pairs.forEach((icon, i) => {
    deck.push(
      { id: `card-${i}-a`, icon, matched: false },
      { id: `card-${i}-b`, icon, matched: false }
    );
  });

  // Fisher-Yates shuffle
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }

  return deck;
}

function MemoryGame({ canPlay = true, onWin = () => {}, currentRound = 1 }) {
  const [cards, setCards] = useState(() => createShuffledDeck());
  const [flipped, setFlipped] = useState([]); // índices de las cartas volteadas (max 2)
  const [isChecking, setIsChecking] = useState(false);
  const [matchedCount, setMatchedCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasTriggeredWin, setHasTriggeredWin] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [lastRound, setLastRound] = useState(currentRound);
  const MAX_COMPLETIONS = 5;
  const FLIP_BACK_DELAY = 3000; // 3 segundos

  const resetGame = useCallback(() => {
    setCards(createShuffledDeck());
    setFlipped([]);
    setIsChecking(false);
    setMatchedCount(0);
    setIsComplete(false);
    setHasTriggeredWin(false);
  }, []);

  // Resetear contador cuando cambie de ronda
  useEffect(() => {
    if (currentRound !== lastRound) {
      setLastRound(currentRound);
      setCompletedCount(0);
      resetGame();
    }
  }, [currentRound, lastRound, resetGame]);

  const handleCardClick = (index) => {
    if (!canPlay || isChecking || isComplete || completedCount >= MAX_COMPLETIONS) return;
    if (flipped.includes(index)) return; // ya está volteada

    const card = cards[index];
    if (card.matched) return; // ya encontrada

    const newFlipped = [...flipped, index];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setIsChecking(true);

      const [i1, i2] = newFlipped;
      const c1 = cards[i1];
      const c2 = cards[i2];
      let newCards = [...cards];

      if (c1.icon === c2.icon) {
        // Es un par - marcar inmediatamente sin delay
        newCards[i1] = { ...c1, matched: true };
        newCards[i2] = { ...c2, matched: true };
        setCards(newCards);

        const newMatchedCount = matchedCount + 1;
        setMatchedCount(newMatchedCount);

        if (newMatchedCount === 8) {
          setIsComplete(true);
          if (!hasTriggeredWin) {
            setHasTriggeredWin(true);
            onWin();
            
            const newCompletedCount = completedCount + 1;
            setCompletedCount(newCompletedCount);
            
            // Auto-reiniciar después de 2 segundos si no alcanzó el límite
            if (newCompletedCount < MAX_COMPLETIONS) {
              setTimeout(() => {
                resetGame();
              }, 2000);
            }
          }
        }

        // Resetear inmediatamente para pares
        setFlipped([]);
        setIsChecking(false);
      } else {
        // No es par - esperar 3 segundos antes de voltear
        setTimeout(() => {
          setFlipped([]);
          setIsChecking(false);
        }, FLIP_BACK_DELAY);
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <span style={styles.title}>
          Memory Game ({completedCount}/{MAX_COMPLETIONS})
        </span>
        <button style={styles.resetButton} type="button" onClick={resetGame}>
          Reiniciar
        </button>
      </div>

      <div style={styles.grid}>
        {cards.map((card, index) => {
          const isFlipped = flipped.includes(index) || card.matched;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => handleCardClick(index)}
              disabled={!canPlay || isChecking || isComplete || completedCount >= MAX_COMPLETIONS}
              style={{
                ...styles.card,
                backgroundColor: isFlipped ? "#0F1C2E" : "#07101e",
                borderColor: card.matched
                  ? "rgba(77,183,255,0.9)"
                  : "rgba(0,155,255,0.45)",
                boxShadow: card.matched
                  ? "0 0 10px rgba(77,183,255,0.8)"
                  : "none",
              }}
            >
              {isFlipped ? (
                <span style={styles.icon}>{card.icon}</span>
              ) : (
                <span style={styles.back} />
              )}
            </button>
          );
        })}
      </div>

      <div style={styles.footer}>
        <span style={styles.info}>
          Pairs encontrados: {matchedCount}/8
        </span>
        {isComplete && completedCount < MAX_COMPLETIONS && (
          <span style={styles.complete}>¡Completado! Reiniciando... 🎉</span>
        )}
        {completedCount >= MAX_COMPLETIONS && (
          <span style={styles.maxReached}>✅ Límite alcanzado (5/5)</span>
        )}
        {!canPlay && (
          <span style={styles.info}>La ronda no está activa.</span>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    background: "rgba(0, 140, 255, 0.05)",
    borderRadius: 8,
    border: "1px solid rgba(0,155,255,0.30)",
    padding: 8,
    fontSize: 12,
    color: "#E8F6FF",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  title: {
    fontWeight: 600,
    fontSize: 12,
  },
  resetButton: {
    borderRadius: 999,
    border: "none",
    padding: "4px 8px",
    fontSize: 11,
    cursor: "pointer",
    background: "rgba(0, 155, 255, 0.2)",
    color: "#A9D9FF",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 6,
    marginTop: 6,
  },
  card: {
    aspectRatio: "3 / 4",
    borderRadius: 6,
    border: "1px solid rgba(0,155,255,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    outline: "none",
  },
  icon: {
    fontSize: 20,
  },
  back: {
    width: "70%",
    height: "70%",
    borderRadius: 4,
    background:
      "linear-gradient(135deg, rgba(0,155,255,0.3), rgba(77,183,255,0.6))",
  },
  footer: {
    marginTop: 6,
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    flexWrap: "wrap",
    gap: 4,
  },
  info: {
    color: "#A9D9FF",
  },
  complete: {
    color: "#4DB7FF",
    fontWeight: 600,
  },
  maxReached: {
    color: "#5DB6FF",
    fontWeight: 600,
  },
};

export default MemoryGame;
